import Modal from "react-modal";
import { useState } from "react";

type Props = {
  isOpen: boolean;
  title: string;
  content: string[];
  buttonText: string | JSX.Element;
  image: string;
  imagePlacement: 'top' | 'bottom';
  onClose: () => void;
  onButtonClick: () => void;
  closeModal: () => void;
  isLoading?: boolean; // Optional isLoading parameter
  showCloseButton?: boolean; // Optional parameter to show/hide close button
};

const GenericModal = ({
  isOpen,
  title,
  content,
  buttonText,
  image,
  imagePlacement,
  onClose,
  onButtonClick,
  closeModal,
  isLoading,
  showCloseButton = true
}: Props) => {
  // State Variables
  const [imageSvg] = useState(image.includes(".svg") || false);
  return (
    <Modal
      isOpen={isOpen}
      contentLabel="Generic Modal"
      className="fixed inset-0 flex items-center justify-center z-50"
      overlayClassName="fixed inset-0 bg-gray-900 bg-opacity-50 z-40"
    >
      <div data-testid="transition-modal" className="m-2 z-30">
        <div className="bg-light rounded-lg text-center overflow-hidden container mx-auto px-4 py-8 relative"
          style={{
            maxWidth: `${window.innerWidth < 672 ? window.innerWidth - 16 + 'px' : "672px"}`,
            maxHeight: `${window.innerHeight}px`,
          }}
        >
          {showCloseButton && (
            <button
              id="closeModal"
              onClick={() => {
                onClose();
                closeModal();
              }}
              className="absolute right-5 top-5 mb-4 md:w-6 md:h-6 w-4 h-4"
            >
              <svg
                width="100%"
                height="100%"
                viewBox="0 0 26 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M23.3768 24L13.225 14.4928L3.07323 24L0.806641 21.8806L10.9744 12.3883L0.806641 2.896L3.07323 0.776642L13.225 10.2839L23.3768 0.791567L25.6274 2.896L15.4757 12.3883L25.6274 21.8806L23.3768 24Z"
                  fill="#2E2E2E"
                />
              </svg>
            </button>
          )}

          <div className="custom-scrollbar overflow-auto pr-2"
            style={{
              maxHeight: `${window.innerHeight - 100}px`,
            }}
          >
            {image !== "" && imagePlacement === 'top' && (
              imageSvg
                ? <img
                    src={image}
                    alt="Modal"
                    className="mx-auto mb-4 p-6 w-full"
                    style={{ width: '200px', height: '200px' }}
                  />
                : <img
                    src={image}
                    alt="Modal"
                    className="mx-auto mb-4 w-70 h-70 p-6"
                    style={{ maxWidth: '350px' }}
                  />
            )}

            <h1 className="mb-2 lg:text-[32px] md:text-[26px] sm:text-[26px] font-bold">{title}</h1>
            {content.map((text, index) => (
              <p
                key={index}
                className="text-gray-500 font-poppins md:text-xl text-base md:mb-9 mb-5"
              >
                {text}
              </p>
            ))}

            {image !== "" && imagePlacement === 'bottom' && (
              <img
                src={image}
                alt="Modal"
                className="mx-auto mb-4 w-70 h-70 p-6"
                style={{ maxWidth: '350px' }}
              />
            )}

            <button
              data-testid="modal-button"
              className="block mx-auto px-8 py-3 border border-transparent w-full max-w-[409px] text-base font-medium rounded-lg shadow-sm text-white bg-secondary-500 hover:bg-secondary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-500"
              onClick={() => {
                onButtonClick();
                onClose();
                closeModal();
              }}
            >
              {isLoading ? (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <svg
                    aria-hidden="true"
                    className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600 mr-2"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor"
                    ></path>
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"
                    ></path>
                  </svg>
                  {" Dein Account wird aktualisiert..."}
                </div>
              ) : buttonText}
            </button>
          </div>
        </div>
      </div>

      <style>{` 
        .custom-scrollbar::-webkit-scrollbar { 
          width: 10px; 
        } 
        .custom-scrollbar::-webkit-scrollbar-track { 
          background: transparent; 
        } 
        .custom-scrollbar::-webkit-scrollbar-thumb { 
          background: #888; 
          border-radius: 10px; 
        } 
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { 
          background: #555; 
        } 
        /* Buttons */ 
        .custom-scrollbar::-webkit-scrollbar-button { 
          display: none; 
        } 
      `}</style>
    </Modal>
  );
};

export default GenericModal;